import { writePreferences } from './cardVisibilityStorage'
import {
  BOARD_V2_CARD_STORAGE_KEY,
  hydrateFromStorage as hydrateBoardV2Cards,
  PersistentBoardV2CardState,
  readStoredBoardV2Cards,
  restoreFromStorage as restoreBoardV2Cards,
} from './persistentBoardV2CardSlice'
import {
  hydrateFromStorage as hydrateMasterRoutePreferences,
  MASTER_ROUTE_STORAGE_KEY,
  PersistentMasterRouteState,
  readStoredMasterRoutePreferences,
  restoreFromStorage as restoreMasterRoutePreferences,
} from './persistentMasterRouteSlice'

import type { AnyAction } from 'redux'

// Focus, not the `storage` event — rearranging a board mid-interaction is worse than a stale toggle.

interface PreferenceState {
  persistentBoardV2Card?: PersistentBoardV2CardState
  persistentMasterRoute?: PersistentMasterRouteState
}

interface PreferenceStore {
  getState: () => PreferenceState
  subscribe: (listener: () => void) => () => void
  dispatch: (action: AnyAction) => unknown
}

export const startCardVisibilitySync = (store: PreferenceStore): (() => void) => {
  const hydrate = (): void => {
    store.dispatch(hydrateBoardV2Cards(readStoredBoardV2Cards()))
    store.dispatch(hydrateMasterRoutePreferences(readStoredMasterRoutePreferences()))
  }
  const onVisibilityChange = (): void => {
    if (!document.hidden) hydrate()
  }

  // Write the startup value straight back: it may come from persist:root, which redux-persist drops on its next write.
  const boardV2AtStartup = readStoredBoardV2Cards()
  const masterRouteAtStartup = readStoredMasterRoutePreferences()
  store.dispatch(restoreBoardV2Cards(boardV2AtStartup))
  store.dispatch(restoreMasterRoutePreferences(masterRouteAtStartup))
  writePreferences(BOARD_V2_CARD_STORAGE_KEY, boardV2AtStartup)
  writePreferences(MASTER_ROUTE_STORAGE_KEY, masterRouteAtStartup)

  let boardV2Cards = store.getState().persistentBoardV2Card
  let masterRoutePreferences = store.getState().persistentMasterRoute

  const unsubscribe = store.subscribe(() => {
    const state = store.getState()
    if (state.persistentBoardV2Card !== boardV2Cards) {
      boardV2Cards = state.persistentBoardV2Card
      if (boardV2Cards) writePreferences(BOARD_V2_CARD_STORAGE_KEY, boardV2Cards)
    }
    if (state.persistentMasterRoute !== masterRoutePreferences) {
      masterRoutePreferences = state.persistentMasterRoute
      if (masterRoutePreferences) writePreferences(MASTER_ROUTE_STORAGE_KEY, masterRoutePreferences)
    }
  })

  if (typeof window === 'undefined') return unsubscribe

  window.addEventListener('focus', hydrate)
  document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    unsubscribe()
    window.removeEventListener('focus', hydrate)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
